import './style.scss';
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import { useEffect } from 'react';
import useState from '../../hooks/useState';
import { Box } from '@mui/material';
import Arrow from './_Arrow';


interface ISliderProps {
    items: JSX.Element[];
    arrows?: boolean;
    dots?: boolean;
    properties?: {
        initial?: number;
        perView?: number;
        interval?: number;
        vertical?: boolean;
    }
}


interface IStateValues {
    perView: number;
    current: number;
    loaded: boolean;
}


const Slider: React.FC<ISliderProps> = ({
    items,
    arrows = false,
    dots = false,
    properties = undefined
}) => {
    const { initial, perView, interval, vertical } = {
        initial: 0,
        perView: 4,
        interval: 30_000,
        vertical: false,
        ...properties
    };


    const state = useState<IStateValues>({
        perView: perView,
        current: 0,
        loaded: false
    });


    const [sliderRef, instanceRef] = useKeenSlider({
        loop: true,
        initial: 0,
        slides: {
            perView: state.values.perView,
            spacing: state.values.perView === 1 ? 0 : 20,
        },
        vertical: vertical,
        slideChanged(slider) {
            state.update('current', slider.track.details.rel);
        },
        created(s) {
            state.update('loaded', true);
        },
    }, [
        (slider) => {
            if (interval === 0) {
                return;
            }
            
            let timeout = undefined as any;
            let mouseOver = false;
            
            function clearNextTimeout() {
                clearTimeout(timeout);
            }
          
            function nextTimeout() {
                clearTimeout(timeout);

                if (mouseOver) {
                    return;
                }

                timeout = setTimeout(() => {
                    slider.next()
                }, interval);
            }

            slider.on("created", () => {
                slider.container.addEventListener("mouseover", () => {
                    mouseOver = true;
                    clearNextTimeout();
                });

                slider.container.addEventListener("mouseout", () => {
                    mouseOver = false;
                    nextTimeout();
                });
                nextTimeout();
            });

            slider.on("dragStarted", clearNextTimeout);
            slider.on("animationEnded", nextTimeout);
            slider.on("updated", nextTimeout);
        },
    ]);


    useEffect(() => {
        const handleResize = () => {
            if (instanceRef.current) {
                const width = window.innerWidth;

                if (width <= 600) {
                    state.update('perView', 1);
                } else if (width <= 900) {
                    state.update('perView', Math.max(perView / 2, 1));
                } else {
                    state.update('perView', perView);
                }

                instanceRef.current.update();
            }
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);

        // eslint-disable-next-line
    }, [perView, instanceRef]);


    useEffect(() => {
        if (instanceRef.current) {
            instanceRef.current.moveToIdx(initial);
        }
    }, [instanceRef, initial]);

    
    return <Box className='slider-default'>
        <Box ref={sliderRef} className="keen-slider">
            {
                items.map((item, index) => {
                    return <Box className="keen-slider__slide" key={index}>
                        {item}
                    </Box>;
                })
            }
        </Box>

        {
            state.values.loaded && instanceRef.current && arrows && <>
                <Arrow 
                    align='left' 
                    onClick={e => {
                        e.stopPropagation();
                        instanceRef.current?.prev();
                    }} 
                />
                    
                <Arrow 
                    align='right' 
                    onClick={e => {
                        e.stopPropagation();
                        instanceRef.current?.next()
                    }} 
                />
            </>
        }

        {
            state.values.loaded && instanceRef.current && dots && (
                <Box className="dots">
                    {
                        [...Array(instanceRef.current.track.details.slides.length).keys(),].map((index) => {
                            return (
                                <button
                                    key={index}
                                    onClick={() => instanceRef.current?.moveToIdx(index)}
                                    className={`dot ${state.values.current === index ? 'active' : ''}`}
                                />
                            );
                        })
                    }
                </Box>
            )
        }
    </Box>;
}

export default Slider;